/* eslint-disable max-lines */
import { EventBus, Events } from "jiffy-store";
import { sleep } from "common/utils/helpers/delay";
import { track } from "common/utils/mixpanel/tracker";

// Re-export Events as EVENTS for test compatibility
export const EVENTS = Events;

// Delay before publishing events to ensure DOM updates are complete
const DELAY_MS = 100;
// Duration of the slide panel closing animation, must match CSS transition duration
const ANIMATION_DURATION = 300;
// CSS class applied during the closing animation to trigger slide-out effect
const CLOSING_CLASS = "slide-panel--closing";

export default class SlidePanel {
  // Configuration options
  #config = {
    panelId: "default",
    closeOnBackdrop: true,
    closeOnEscape: true,
    onOpen: null,
    onClose: null,
    onBeforeOpen: null,
    onBeforeClose: null,
    trackDismissal: false,
    mixpanelEvent: null,
    mixpanelProperties: {}
  };

  // Whether the panel is currently open
  #isOpen;

  // Whether the panel is currently closing
  #isClosing;

  // Event handlers
  #eventHandlers;

  // DOM event listener references for cleanup
  #closeButtonHandler;

  #backdropClickHandler;

  #cancelHandler;

  // Original body overflow value
  #originalBodyOverflow;

  // Track dismissal method for analytics
  #dismissalMethod = null;

  constructor(options = {}) {
    this.#config = {
      ...this.#config,
      ...options
    };

    this.#isOpen = false;
    this.#isClosing = false;
    this.#eventHandlers = {};
    this.#originalBodyOverflow = null;

    this.#init();
  }

  /**
   * Initializes the panel by finding DOM elements and binding events
   * @private
   * @returns {void}
   */
  #init() {
    this.#bindEvents();
  }

  // get the panel element
  get #$panel() {
    return document.querySelector(`.js-slide-panel[data-panel-id="${this.#config.panelId}"]`);
  }

  // get the content element of the panel
  get #$content() {
    return this.#$panel?.querySelector(".js-slide-panel-content");
  }

  // get the close buttons of the panel
  get #$closeButtons() {
    return this.#$panel?.querySelectorAll(".js-slide-panel-close");
  }

  /**
   * Binds all event listeners for close buttons, backdrop clicks, and escape key
   * @private
   * @returns {void}
   */
  #bindEvents() {
    if (!this.#$panel) return;

    this.#closeButtonHandler = e => {
      e.preventDefault();
      this.close("close_button");
    };

    this.#$closeButtons?.forEach($button => {
      $button.addEventListener("click", this.#closeButtonHandler);
    });

    if (this.#config.closeOnBackdrop) {
      this.#backdropClickHandler = e => {
        // Only close when clicking the backdrop itself, not when clicking panel content
        if (e.target === this.#$panel) {
          this.close("outside_click");
        }
      };
      this.#$panel.addEventListener("click", this.#backdropClickHandler);
    }

    if (this.#config.closeOnEscape) {
      this.#cancelHandler = e => {
        e.preventDefault();
        this.close("esc_key");
      };
      this.#$panel.addEventListener("cancel", this.#cancelHandler);
    }
  }

  /**
   * Unbinds all event listeners
   * @private
   * @returns {void}
   */
  #unbindEvents() {
    this.#$closeButtons?.forEach($button => {
      $button.removeEventListener("click", this.#closeButtonHandler);
    });

    if (this.#backdropClickHandler) {
      this.#$panel?.removeEventListener("click", this.#backdropClickHandler);
    }

    if (this.#cancelHandler) {
      this.#$panel?.removeEventListener("cancel", this.#cancelHandler);
    }
  }

  /**
   * Prevents body scrolling by setting overflow to hidden
   * Stores the original overflow value for restoration
   * @private
   * @returns {void}
   */
  // eslint-disable-next-line class-methods-use-this
  #preventBodyScroll() {
    document.documentElement.classList.add("locked-all");
  }

  /**
   * Restores body scrolling to its original state
   * @private
   * @returns {void}
   */
  // eslint-disable-next-line class-methods-use-this
  #restoreBodyScroll() {
    document.documentElement.classList.remove("locked-all");
  }

  /**
   * Opens the slide panel
   * Triggers onBeforeOpen callback, shows the modal, prevents body scroll,
   * triggers onOpen callback, and publishes SLIDE_PANEL_OPENED event
   * @param {string|HTMLElement} [content] - Optional content to set before opening
   * @public
   * @returns {void}
   */
  open(content) {
    // Prevent opening if panel is already open or currently closing to avoid animation conflicts
    if (!this.#$panel || this.#isOpen || this.#isClosing) return;

    if (this.#config.onBeforeOpen) {
      this.#config.onBeforeOpen(this);
    }

    this.#trigger("beforeOpen", { panel: this });

    if (content) {
      this.setContent(content);
    }

    // Use native dialog showModal() to leverage browser's modal behavior and focus trapping
    this.#$panel.showModal();
    this.#isOpen = true;
    this.#preventBodyScroll();

    if (this.#config.onOpen) {
      this.#config.onOpen(this);
    }

    // Delay event publishing to ensure all DOM updates and animations have started
    sleep(DELAY_MS).then(() => {
      EventBus.publish(Events.SLIDE_PANEL_OPENED, null, { slidePanel: this });
      this.#trigger("open", { panel: this });
    });
  }

  /**
   * Closes the slide panel with animation
   * Triggers onBeforeClose callback, adds closing class, closes the modal after animation,
   * restores body scroll, triggers onClose callback, and publishes SLIDE_PANEL_CLOSED event
   * @param {string} [dismissalMethod=null] - How panel was dismissed: "close_button" | "outside_click" | "esc_key"
   * @public
   * @returns {void}
   */
  close(dismissalMethod = null) {
    if (!this.#$panel || !this.#isOpen || this.#isClosing) return;

    this.#dismissalMethod = dismissalMethod;
    this.#isClosing = true;

    if (this.#config.onBeforeClose) {
      this.#config.onBeforeClose(this);
    }

    this.#trigger("beforeClose", { panel: this });

    // Add closing class to trigger CSS slide-out animation
    this.#$panel.classList.add(CLOSING_CLASS);

    // Wait for animation to complete before actually closing the dialog
    setTimeout(() => {
      // Check if panel still exists (might have been removed by destroy)
      if (!this.#$panel) {
        this.#isOpen = false;
        this.#isClosing = false;
        return;
      }

      this.#$panel.close();
      this.#$panel.classList.remove(CLOSING_CLASS);
      this.#isOpen = false;
      this.#isClosing = false;
      this.#restoreBodyScroll();

      // Track dismissal if configured
      if (this.#config.trackDismissal) {
        this.#trackDismissal();
      }

      if (this.#config.onClose) {
        this.#config.onClose(this);
      }

      // Delay event publishing to ensure cleanup is complete
      sleep(DELAY_MS).then(() => {
        EventBus.publish(Events.SLIDE_PANEL_CLOSED, null, { slidePanel: this });
        this.#trigger("close", { panel: this });
      });
    }, ANIMATION_DURATION);
  }

  /**
   * Tracks slide panel dismissal to Mixpanel
   * @private
   * @returns {void}
   */
  #trackDismissal() {
    if (!this.#config.mixpanelEvent) return;

    track(this.#config.mixpanelEvent, {
      dismissal_method: this.#dismissalMethod || "close_button",
      ...this.#config.mixpanelProperties
    });

    // Reset after tracking
    this.#dismissalMethod = null;
  }

  /**
   * Sets the content of the slide panel
   * Accepts either a string (HTML) or an HTMLElement
   * @param {string|HTMLElement} content - Content to set in the panel
   * @public
   * @returns {void}
   */
  setContent(content) {
    if (!this.#$content) return;

    if (typeof content === "string") {
      this.#$content.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      this.#$content.innerHTML = "";
      this.#$content.appendChild(content);
    }
  }

  /**
   * Gets the content element of the slide panel
   * @public
   * @returns {HTMLElement|null} The content element or null if not found
   */
  getContent() {
    return this.#$content;
  }

  /**
   * Destroys the slide panel
   * Closes the panel if open, clears event handlers, and removes the panel from DOM
   * @public
   * @returns {void}
   */
  destroy() {
    if (this.#isOpen) {
      this.close();
    }

    this.#unbindEvents();
    this.#eventHandlers = {};

    if (this.#$panel) {
      this.#$panel.remove();
    }
  }

  // Public getters for testing
  get config() {
    return this.#config;
  }

  get isOpen() {
    return this.#isOpen;
  }

  set isOpen(value) {
    this.#isOpen = value;
  }

  get isClosing() {
    return this.#isClosing;
  }

  set isClosing(value) {
    this.#isClosing = value;
  }

  get $panel() {
    return this.#$panel;
  }

  // eslint-disable-next-line class-methods-use-this
  set $panel(value) {
    // For testing purposes only - no-op as this is computed
  }

  get $content() {
    return this.#$content;
  }

  // eslint-disable-next-line class-methods-use-this
  set $content(value) {
    // For testing purposes only - no-op as this is computed
  }

  get $closeButtons() {
    return this.#$closeButtons;
  }

  get eventHandlers() {
    return this.#eventHandlers;
  }

  get originalBodyOverflow() {
    return this.#originalBodyOverflow;
  }

  set originalBodyOverflow(value) {
    this.#originalBodyOverflow = value;
  }

  // Public method to restore body scroll for testing
  restoreBodyScroll() {
    this.#restoreBodyScroll();
  }

  // Public method to trigger events for testing
  trigger(event, data) {
    this.#trigger(event, data);
  }

  /**
   * Subscribes to a panel event
   * @param {string} event - Event name to subscribe to
   * @param {Function} callback - Callback function to execute when event is triggered
   * @public
   * @returns {void}
   */
  on(event, callback) {
    if (!this.#eventHandlers[event]) {
      this.#eventHandlers[event] = [];
    }
    this.#eventHandlers[event].push(callback);
  }

  /**
   * Unsubscribes from a panel event
   * @param {string} event - Event name to unsubscribe from
   * @param {Function} callback - Callback function to remove
   * @public
   * @returns {void}
   */
  off(event, callback) {
    if (!this.#eventHandlers[event]) return;

    this.#eventHandlers[event] = this.#eventHandlers[event].filter(cb => cb !== callback);
  }

  /**
   * Triggers a panel event
   * Executes all callbacks subscribed to the event
   * @param {string} event - Event name to trigger
   * @param {Object} data - Event data to pass to callbacks
   * @private
   * @returns {void}
   */
  #trigger(event, data) {
    if (!this.#eventHandlers[event]) return;

    this.#eventHandlers[event].forEach(callback => {
      callback(data);
    });
  }
}
